import mqtt from 'mqtt';

/**
 * Singleton MQTT Service
 * Manages WebSocket MQTT connection and message handling
 */
class MQTTService {
  constructor() {
    if (MQTTService.instance) {
      return MQTTService.instance;
    }

    this.client = null;
    this.isConnected = false;
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 5000; // 5 seconds
    this.subscribedTopics = new Set(); // Track subscribed topics
    this.connectionOptions = {
      protocol: 'wss',
      host: 'mqtt.bridgex.global',
      port: 443,
      path: '/mqtt',
      username: 'malbon',
      password: 'admin_malbon_123',
      keepalive: 60,
      clean: true,
      reconnectPeriod: 0, // We handle reconnection manually
      connectTimeout: 30 * 1000, // 30 seconds
      clientId: `warehouse-web-${Math.random().toString(16).substr(2, 8)}`,
    };

    MQTTService.instance = this;
  }

  /**
   * Connect to MQTT broker via WebSocket
   */
  async connect() {
    if (this.isConnected || this.isConnecting) {
      return;
    }

    try {
      this.isConnecting = true;

      const brokerUrl = `${this.connectionOptions.protocol}://${this.connectionOptions.host}${this.connectionOptions.path}`;
      
      this.client = mqtt.connect(brokerUrl, {
        ...this.connectionOptions,
        // WebSocket specific options
        wsOptions: {
          // Add any WebSocket specific options here if needed
        }
      });

      this.setupEventHandlers();
      
    } catch (error) {
      console.error('[MQTT] Connection error:', error);
      this.isConnecting = false;
      this.handleReconnection();
    }
  }

  /**
   * Setup MQTT client event handlers
   */
  setupEventHandlers() {
    if (!this.client) return;

    // Connection successful
    this.client.on('connect', () => {
      this.isConnected = true;
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      
      
      // No automatic subscription - topics will be subscribed on demand
    });

    // Connection error
    this.client.on('error', (error) => {
      console.error('[MQTT] Connection error:', error);
      this.isConnected = false;
      this.isConnecting = false;
      this.handleReconnection();
    });

    // Connection closed
    this.client.on('close', () => {
      this.isConnected = false;
      this.isConnecting = false;
      this.handleReconnection();
    });

    // Connection offline
    this.client.on('offline', () => {
      this.isConnected = false;
    });

    // Message received
    this.client.on('message', (topic, message, packet) => {
      try {
        const messageStr = message.toString();        
        
        // Try to parse as JSON, fallback to string
        let parsedMessage;
        try {
          parsedMessage = JSON.parse(messageStr);
        } catch {
          parsedMessage = messageStr;
        }
                
        // Emit custom event for other parts of the app to listen
        this.emitMessageEvent(topic, parsedMessage, packet);
        
      } catch (error) {
        console.error('[MQTT] Error processing message:', error);
      }
    });

    // Reconnection
    this.client.on('reconnect', () => {
      this.isConnecting = true;
    });
  }

  /**
   * Subscribe to specific topic
   */
  subscribe(topic, callback) {
    if (!this.client || !this.isConnected) {
      console.warn('[MQTT] Cannot subscribe - not connected');
      return false;
    }

    try {
      this.client.subscribe(topic, (error) => {
        if (error) {
          console.error(`[MQTT] Subscription error for topic "${topic}":`, error);
        } else {
          this.subscribedTopics.add(topic);
        }
      });
      return true;
    } catch (error) {
      console.error('[MQTT] Error subscribing to topic:', error);
      return false;
    }
  }

  /**
   * Handle reconnection logic
   */
  handleReconnection() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[MQTT] Max reconnection attempts reached. Stopping reconnection.');
      return;
    }

    this.reconnectAttempts++;

    setTimeout(() => {
      if (!this.isConnected && !this.isConnecting) {
        this.connect();
      }
    }, this.reconnectInterval);
  }

  /**
   * Emit custom message event for other parts of the app
   */
  emitMessageEvent(topic, message, packet) {
    // Create custom event
    const event = new CustomEvent('mqttMessage', {
      detail: {
        topic,
        message,
        packet,
        timestamp: new Date().toISOString()
      }
    });
    
    // Dispatch event on window object
    window.dispatchEvent(event);
  }

  /**
   * Disconnect from MQTT broker
   */
  disconnect() {
    if (this.client && this.isConnected) {
      this.client.end();
      this.isConnected = false;
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      this.subscribedTopics.clear(); // Clear subscribed topics
    }
  }

  /**
   * Publish message to a topic
   */
  publish(topic, message, options = {}) {
    if (!this.client || !this.isConnected) {
      console.warn('[MQTT] Cannot publish - not connected');
      return false;
    }

    try {
      const messageStr = typeof message === 'string' ? message : JSON.stringify(message);
      this.client.publish(topic, messageStr, options);
      return true;
    } catch (error) {
      console.error('[MQTT] Error publishing message:', error);
      return false;
    }
  }

  /**
   * Subscribe to specific topic
   */
  subscribe(topic, callback) {
    if (!this.client || !this.isConnected) {
      console.warn('[MQTT] Cannot subscribe - not connected');
      return false;
    }

    try {
      this.client.subscribe(topic, (error) => {
        if (error) {
          console.error(`[MQTT] Subscription error for topic "${topic}":`, error);
        }
      });
      return true;
    } catch (error) {
      console.error('[MQTT] Error subscribing to topic:', error);
      return false;
    }
  }

  /**
   * Unsubscribe from specific topic
   */
  unsubscribe(topic) {
    if (!this.client || !this.isConnected) {
      console.warn('[MQTT] Cannot unsubscribe - not connected');
      return false;
    }

    try {
      this.client.unsubscribe(topic, (error) => {
        if (error) {
          console.error(`[MQTT] Unsubscription error for topic "${topic}":`, error);
        } else {
          this.subscribedTopics.delete(topic);
        }
      });
      return true;
    } catch (error) {
      console.error('[MQTT] Error unsubscribing from topic:', error);
      return false;
    }
  }

  /**
   * Get connection status
   */
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      isConnecting: this.isConnecting,
      reconnectAttempts: this.reconnectAttempts,
      clientId: this.connectionOptions.clientId,
      subscribedTopics: Array.from(this.subscribedTopics)
    };
  }

  /**
   * Get list of subscribed topics
   */
  getSubscribedTopics() {
    return Array.from(this.subscribedTopics);
  }

  /**
   * Check if a topic is subscribed
   */
  isTopicSubscribed(topic) {
    return this.subscribedTopics.has(topic);
  }

  /**
   * Reset reconnection attempts (useful for manual reconnection)
   */
  resetReconnectionAttempts() {
    this.reconnectAttempts = 0;
  }
}

// Create and export singleton instance
const mqttService = new MQTTService();
export default mqttService;